import { CalendarIcon } from "lucide-react";
import { DateTime } from "luxon";
import React, { useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { useAppointmentViewContext } from "@/domains/appointment/appointments/shared/hooks/use-appointment-view-context";
import { getDateRangeForView } from "@/domains/appointment/appointments/utils/get-date-range-for-view";
import type { AppointmentBookingStatus } from "@/gql/graphql";
import { useCalendarAppointments, useCalendarWorkers } from "../hooks";
import { useCalendarFilters } from "../hooks/use-calendar-filters";
import { useCalendarStateStore } from "../store/use-calendar-state-store";
import type { CalendarViewType } from "../types";
import { CalendarViewRenderer } from "./calendar-view-renderer";

interface CalendarViewProps {
  viewType: CalendarViewType;
  currentDate: DateTime;
  onAppointmentClick?: (appointmentId: string) => void;
  onAppointmentDrop?: (
    appointmentId: string,
    newDate: DateTime,
    newWorkerId?: string | null
  ) => void;
}

export const CalendarView: React.FC<CalendarViewProps> = ({
  viewType,
  currentDate,
  onAppointmentClick,
  onAppointmentDrop,
}) => {
  // Get setCurrentDate from store for navigation functionality
  const { setCurrentDate } = useCalendarStateStore();

  // Get filters from unified store
  const { statusFilters, workerFilters } = useCalendarFilters();

  // Calculate date range based on viewType and currentDate
  const { startDate, endDate } = useMemo(
    () => getDateRangeForView(currentDate, viewType),
    [currentDate, viewType]
  );

  // Fetch data using direct GraphQL hooks
  const { appointments, loading: appointmentsLoading } = useCalendarAppointments({
    startDate,
    endDate,
    statusFilters: statusFilters as AppointmentBookingStatus[],
    workerFilters,
  });

  const { workers, loading: workersLoading } = useCalendarWorkers();

  const isLoading =
    (appointmentsLoading && !appointments?.length) || (workersLoading && !workers?.length);

  // Register view context for AI assistant
  useAppointmentViewContext({
    key: "calendar",
    viewType: `Calendar/${viewType}`,
    viewIcon: "📅",
    currentDate,
    startDate,
    endDate,
    appointments,
    statusFilters: statusFilters as AppointmentBookingStatus[],
    workerFilters,
  });

  return (
    <Card className="rounded-b-lg bg-clip-content overflow-hidden h-full">
      <CardContent className="p-0 relative h-full">
        {/* Loading overlay */}
        {isLoading && (
          <div className="absolute inset-0 bg-background/10 backdrop-blur-sm z-10 flex items-center justify-center">
            <div className="flex items-center justify-center gap-3 text-muted-foreground">
              <CalendarIcon className="h-5 w-5 animate-pulse" />
              <span>Loading calendar...</span>
            </div>
          </div>
        )}

        <CalendarViewRenderer
          viewType={viewType}
          currentDate={currentDate}
          onDateChange={setCurrentDate}
          onAppointmentClick={onAppointmentClick}
          onAppointmentDrop={onAppointmentDrop}
        />
      </CardContent>
    </Card>
  );
};
